import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  CircularProgress,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Switch,
  useTheme,
} from "@mui/material";
import api from "../../utils/api";

const RolesPage = () => {
  const theme = useTheme();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [assignedPermissionIds, setAssignedPermissionIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedRole = roles.find((r) => r.RoleID === selectedRoleId);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await api.get("/roles");
        setRoles(response.data);
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };

    const fetchAllPermissions = async () => {
      try {
        const response = await api.get("/permissions");
        setPermissions(response.data);
      } catch (error) {
        console.error("Error fetching permissions:", error);
      }
    };

    fetchRoles();
    fetchAllPermissions();
  }, []);

  useEffect(() => {
    const fetchPermissionsForRole = async () => {
      if (!selectedRoleId || permissions.length === 0) return;
      setLoading(true);
      try {
        const response = await api.get(`/role-permissions/${selectedRoleId}`);
        const permissionIds = response.data?.permissionIds || [];
        setAssignedPermissionIds(permissionIds);
      } catch (error) {
        console.error("Error loading role permissions:", error);
        setAssignedPermissionIds([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPermissionsForRole();
  }, [selectedRoleId, permissions]);

  const handleRoleClick = (roleId) => {
    setSelectedRoleId(roleId);
  };

  const togglePermission = (permissionId) => {
    setAssignedPermissionIds((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleSavePermissions = async () => {
    if (!selectedRoleId) return;
    setSaving(true);
    try {
      await api.post(`/roles/${selectedRoleId}/permissions`, {
        permissionIds: assignedPermissionIds,
      });
      alert("Permissions updated successfully.");
    } catch (err) {
      console.error("Error saving permissions:", err);
      alert("Failed to save permissions.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 3,
        p: { xs: 2, md: 3 },
      }}
    >
      {/* Roles List */}
      <Paper
        elevation={0}
        sx={{
          width: { xs: "100%", md: 280 },
          borderRadius: "18px",
          border: "1px solid rgba(47,99,173,0.08)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          backgroundColor: "rgba(255,255,255,0.96)",
          p: 2,
          alignSelf: "flex-start",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 1.5,
            fontWeight: 700,
            color: "#223142",
          }}
        >
          Roles
        </Typography>

        <Divider sx={{ mb: 1.5 }} />

        <List dense disablePadding>
          {roles.map((role) => (
            <ListItemButton
              key={role.RoleID}
              onClick={() => handleRoleClick(role.RoleID)}
              selected={selectedRoleId === role.RoleID}
              sx={{
                borderRadius: "12px",
                mt: 0.75,
                "&.Mui-selected": {
                  backgroundColor: "rgba(47,99,173,0.10)",
                  color: "#2f63ad",
                },
                "&.Mui-selected:hover": {
                  backgroundColor: "rgba(47,99,173,0.16)",
                },
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <ListItemText
                primary={role.RoleName}
                primaryTypographyProps={{
                  fontWeight: selectedRoleId === role.RoleID ? 700 : 500,
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Paper>

      {/* Permissions List */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          borderRadius: "18px",
          border: "1px solid rgba(47,99,173,0.08)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          backgroundColor: "rgba(255,255,255,0.96)",
          p: { xs: 2, md: 3 },
          minWidth: 0,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mb: 2,
            color: "#223142",
          }}
        >
          {selectedRole
            ? `Permissions for Role: ${selectedRole.RoleName}`
            : "Select a role to manage permissions"}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : selectedRoleId ? (
          <Box
            sx={{
              border: "1px solid #eef2f7",
              borderRadius: "14px",
              overflow: "hidden",
              backgroundColor: "#fff",
            }}
          >
            <List dense disablePadding>
              {permissions.map((perm, index) => (
                <Box
                  key={perm.PermissionID}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.25,
                    borderBottom:
                      index < permissions.length - 1 ? "1px solid #eef2f7" : "none",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.95rem",
                      color: "#223142",
                      pr: 2,
                    }}
                  >
                    {perm.Description}
                  </Typography>

                  <Switch
                    edge="end"
                    size="medium"
                    color="primary"
                    checked={assignedPermissionIds.includes(perm.PermissionID)}
                    onChange={() => togglePermission(perm.PermissionID)}
                  />
                </Box>
              ))}
            </List>
          </Box>
        ) : (
          <Typography sx={{ mt: 2, color: "text.secondary" }}>
            Choose a role to view its permission settings.
          </Typography>
        )}

        {selectedRoleId && (
          <Box sx={{ textAlign: "right", mt: 4 }}>
            <Button
              variant="contained"
              size="medium"
              onClick={handleSavePermissions}
              disabled={saving}
              sx={{
                backgroundColor: "#2f63ad",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                px: 3,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#1f4f94",
                  boxShadow: "none",
                },
              }}
            >
              {saving ? "Saving..." : "Save Permissions"}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default RolesPage;

